/**
 * LegalAcceptanceGate (PRE_LAUNCH CL1-CL3 / F4).
 *
 * Mounted INSIDE <Router> (because it links to the App Guide via routing)
 * and renders nothing when the current user has already accepted the
 * current legal version. If they haven't, a non-dismissible modal blocks
 * interaction with the app and requires the user to confirm they've read
 * the three documents before the gate clears.
 *
 * The gate fires:
 *   - on first sign-in after registration (user.legalAccepted === false),
 *   - on next sign-in after a LEGAL_VERSION bump (server returns false).
 *
 * Acceptance is one-click after the user has visited (or scrolled through)
 * the three docs. We do not enforce "scroll-to-bottom" — that creates
 * accessibility issues for screen-reader users — but we do require an
 * explicit checkbox tick acknowledging each of the three docs.
 */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Checkbox, FormControlLabel, Alert, Link as MuiLink,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { Link as RouterLink } from 'react-router-dom';
import { RootState } from '../../store';
import { acceptLegal } from '../../store/authSlice';

const LegalAcceptanceGate: React.FC = () => {
  const dispatch = useDispatch<any>();
  const user = useSelector((s: RootState) => s.auth.user);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [risk, setRisk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show only for signed-in users who haven't accepted the current version.
  // When user is null (logged out) or already legalAccepted, render nothing.
  if (!user) return null;
  if ((user as any).legalAccepted === true) return null;

  const allChecked = terms && privacy && risk;

  const handleAccept = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await dispatch(acceptLegal()).unwrap();
    } catch (err: any) {
      setError(err?.message || 'Could not record acceptance. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      // Non-dismissible — disable backdrop close and Escape.
      disableEscapeKeyDown
      onClose={() => { /* swallow */ }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="legal-gate-title"
      aria-describedby="legal-gate-body"
    >
      <DialogTitle id="legal-gate-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GavelIcon /> Before you continue
      </DialogTitle>
      <DialogContent id="legal-gate-body">
        <Typography variant="body2" sx={{ mb: 2 }}>
          Welcome to earngush. Before you place any orders, we need you to confirm
          you've read three short documents:
        </Typography>

        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <MuiLink component={RouterLink} to="/app-guide#terms" target="_blank" rel="noopener" sx={{ fontWeight: 700 }}>
              Terms of Service ↗
            </MuiLink>
            {' '} — what we promise, what we don't, governing law (India).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <MuiLink component={RouterLink} to="/app-guide#privacy" target="_blank" rel="noopener" sx={{ fontWeight: 700 }}>
              Privacy Policy ↗
            </MuiLink>
            {' '} — what data we collect, why, who we share with, your rights under DPDP 2023.
          </Typography>
          <Typography variant="body2">
            <MuiLink component={RouterLink} to="/app-guide#risk-disclaimer" target="_blank" rel="noopener" sx={{ fontWeight: 700 }}>
              Risk Disclaimer ↗
            </MuiLink>
            {' '} — leverage, automation, suitability, what can go wrong.
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Read these before ticking the boxes.</strong> Trading involves real
          financial risk; you can lose money — possibly all of the capital you trade.
        </Alert>

        <FormControlLabel
          control={<Checkbox checked={terms} onChange={(e) => setTerms(e.target.checked)} />}
          label="I have read and agree to the Terms of Service."
        />
        <br />
        <FormControlLabel
          control={<Checkbox checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} />}
          label="I have read and agree to the Privacy Policy."
        />
        <br />
        <FormControlLabel
          control={<Checkbox checked={risk} onChange={(e) => setRisk(e.target.checked)} />}
          label="I have read and understood the Risk Disclaimer."
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={!allChecked || submitting}
          fullWidth
          size="large"
        >
          {submitting ? 'Recording…' : 'I have read and agree — continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalAcceptanceGate;
